import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { memberApi } from '../../services/api';

const MemberForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    referer: '',
    payment_type: '',
    service: '',
    status: 'active',
    paid_by_member_id: '',
    is_free: false,
    monthly_fee: '',
    start_date: new Date().toISOString().split('T')[0]
  });
  const [members, setMembers] = useState([]);
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState('');
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all members for the paid_by_member_id dropdown and to extract services
        const membersResponse = await memberApi.getAll();
        setMembers(membersResponse.data);
        
        // Extract unique services from existing members
        const existingServices = membersResponse.data
          .map(member => member.service)
          .filter(service => service); // Filter out null/undefined
        
        // Combine with default services and remove duplicates
        const uniqueServices = [...new Set([...services, ...existingServices])];
        setServices(uniqueServices);
        
        // If editing, fetch member details
        if (isEditMode) {
          const response = await memberApi.getById(id);
          const member = response.data;
          
          // Get the current fee from the fee history
          let currentFee = '';
          if (member.monthly_fee_history && member.monthly_fee_history.length > 0) {
            currentFee = member.monthly_fee_history[member.monthly_fee_history.length - 1].amount;
          }
          
          setFormData({
            name: member.name || '',
            email: member.email || '',
            username: member.username || '',
            referer: member.referer || '',
            payment_type: member.payment_type || '',
            service: member.service || '',
            status: member.status || 'active',
            paid_by_member_id: member.paid_by_member_id || '',
            is_free: member.is_free || false,
            monthly_fee: currentFee,
            start_date: member.start_date ? new Date(member.start_date).toISOString().split('T')[0] : ''
          });
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load data');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'service' && value === 'new') {
      // If "Add new service" is selected, set service to 'new' to show the input field
      setFormData(prevData => ({
        ...prevData,
        service: 'new'
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };
  
  const handleNewServiceChange = (e) => {
    setNewService(e.target.value);
  };

  const handleNewServiceSubmit = () => {
    if (newService.trim()) {
      // Add the new service to the list
      setServices(prev => [...prev, newService.trim()]);
      
      // Set it as the selected service
      setFormData(prevData => ({
        ...prevData,
        service: newService.trim()
      }));
      
      // Clear the new service input
      setNewService('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert string values to appropriate types
      const processedData = {
        ...formData,
        monthly_fee: formData.is_free ? 0 : parseFloat(formData.monthly_fee),
        start_date: formData.start_date,
        is_free: Boolean(formData.is_free)
      };
      
      // Remove empty strings for optional fields
      Object.keys(processedData).forEach(key => {
        if (processedData[key] === '') {
          processedData[key] = null;
        }
      });
      
      if (isEditMode) {
        await memberApi.update(id, processedData);
      } else {
        await memberApi.create(processedData);
      }
      navigate('/members');
    } catch (err) {
      setError('Failed to save member');
      console.error(err);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h1 className="display-5 mb-3">{isEditMode ? 'Edit Member' : 'Add New Member'}</h1>
          
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
      </div>
      
      <div className="row">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="name" className="form-label fw-bold">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label htmlFor="username" className="form-label fw-bold">Username</label>
                    <input
                      type="text"
                      className="form-control"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="email" className="form-label fw-bold">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label htmlFor="referer" className="form-label fw-bold">Referer</label>
                    <select
                      className="form-select"
                      id="referer"
                      name="referer"
                      value={formData.referer}
                      onChange={handleChange}
                    >
                      <option value="">Select a referer</option>
                      {members.map(member => (
                        <option key={member.id} value={member.username || member.name}>
                          {member.username || member.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <hr className="my-4" />
                <h5 className="mb-3">Subscription Details</h5>
                
                <div className="mb-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="is_free"
                      name="is_free"
                      checked={formData.is_free}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="is_free">
                      Free membership (no payment required)
                    </label>
                  </div>
                </div>
                
                <div className="row">
                  {!formData.is_free && (
                    <>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="payment_type" className="form-label">Payment Type</label>
                        <select
                          className="form-select"
                          id="payment_type"
                          name="payment_type"
                          value={formData.payment_type}
                          onChange={handleChange}
                        >
                          <option value="">Select payment type</option>
                          <option value="PP">PayPal</option>
                          <option value="Ü">Bank Transfer (Überweisung)</option>
                          <option value="+">Paid by another member</option>
                        </select>
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <label htmlFor="monthly_fee" className="form-label">Monthly Fee (€)</label>
                        <div className="input-group">
                          <span className="input-group-text">€</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control"
                            id="monthly_fee"
                            name="monthly_fee"
                            value={formData.monthly_fee}
                            onChange={handleChange}
                            required={!formData.is_free}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="service" className="form-label">Service</label>
                    {formData.service !== 'new' ? (
                      <select
                        className="form-select"
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                      >
                        <option value="">Select a service</option>
                        {services.map((service, index) => (
                          <option key={index} value={service}>{service}</option>
                        ))}
                        <option value="new">+ Add new service</option>
                      </select>
                    ) : (
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="newService"
                          value={newService}
                          onChange={handleNewServiceChange}
                          placeholder="Enter new service name"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleNewServiceSubmit}
                        >
                          <i className="fas fa-plus me-1"></i> Add
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {formData.payment_type === '+' && (
                    <div className="col-md-6 mb-3">
                      <label htmlFor="paid_by_member_id" className="form-label">Paid By</label>
                      <select
                        className="form-select"
                        id="paid_by_member_id"
                        name="paid_by_member_id"
                        value={formData.paid_by_member_id}
                        onChange={handleChange}
                      >
                        <option value="">Select member</option>
                        {members.map(member => (
                          <option key={member.id} value={member.id}>
                            {member.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="status" className="form-label">Status</label>
                    <select
                      className="form-select"
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="active">Active</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label htmlFor="start_date" className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="start_date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-success px-4">
                    <i className="fas fa-save me-2"></i>
                    {isEditMode ? 'Update Member' : 'Create Member'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/members')}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberForm;
